// SVG path data to flattened polylines.
//
// Covers the command set `features/Map` emits — M, L, H, V, A, Z and their
// relative forms, plus the Bézier commands for completeness — and flattens curves
// to line segments. Flattening happens once per distinct `d` string and the
// result is cached, so the tolerance can be fine without costing anything
// per-frame.

const NUMBER_RE = /[-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?/g;
const COMMAND_RE = /([MmLlHhVvCcSsQqTtAaZz])([^MmLlHhVvCcSsQqTtAaZz]*)/g;

// In local user units. Every shape in the map is small (tens of units) and the
// camera rarely exceeds 2x, so a quarter of a unit is well under a pixel.
const FLATTEN_TOLERANCE = 0.12;

const MAX_ARC_SEGMENTS = 128;
const MAX_CURVE_SEGMENTS = 64;

const cache = new Map();
const CACHE_LIMIT = 2048;

// A subpath is `{ points: [x0, y0, x1, y1, ...], closed }`.
export function flattenPath(d) {
  if (!d) return [];

  const cached = cache.get(d);
  if (cached) return cached;

  const result = flattenUncached(d);

  // `d` for the graph traces and the redis memory bar is rebuilt from live data,
  // so the key space is unbounded. Clearing wholesale keeps the static shapes —
  // which are the overwhelming majority — cheap without an eviction order.
  if (cache.size >= CACHE_LIMIT) cache.clear();
  cache.set(d, result);

  return result;
}

function flattenUncached(d) {
  const subpaths = [];
  let points = null;

  let x = 0;
  let y = 0;
  let startX = 0;
  let startY = 0;

  // Reflection points for the smooth curve commands.
  let lastControlX = 0;
  let lastControlY = 0;
  let lastCommand = "";

  const begin = () => {
    points = [];
    subpaths.push({ points, closed: false });
  };

  const lineTo = (nx, ny) => {
    if (!points) begin();
    points.push(nx, ny);
    x = nx;
    y = ny;
  };

  COMMAND_RE.lastIndex = 0;
  let match;

  while ((match = COMMAND_RE.exec(d)) !== null) {
    const command = match[1];
    const raw = match[2].match(NUMBER_RE);
    const args = raw ? raw.map(Number) : [];
    const relative = command >= "a";
    const upper = relative ? command.toUpperCase() : command;

    if (upper === "Z") {
      if (points && points.length >= 2) {
        subpaths[subpaths.length - 1].closed = true;
        x = startX;
        y = startY;
      }
      points = null;
      lastCommand = upper;
      continue;
    }

    // A command may carry several sets of arguments; every set after the first
    // repeats the command, except that a repeated moveto draws lines.
    const stride = ARG_COUNT[upper];
    if (!stride) continue;

    for (let i = 0; i + stride <= args.length; i += stride) {
      const a = args.slice(i, i + stride);

      switch (upper) {
        case "M": {
          const nx = relative ? x + a[0] : a[0];
          const ny = relative ? y + a[1] : a[1];
          if (i === 0) {
            begin();
            points.push(nx, ny);
            x = nx;
            y = ny;
            startX = nx;
            startY = ny;
          } else {
            lineTo(nx, ny);
          }
          break;
        }
        case "L":
          lineTo(relative ? x + a[0] : a[0], relative ? y + a[1] : a[1]);
          break;
        case "H":
          lineTo(relative ? x + a[0] : a[0], y);
          break;
        case "V":
          lineTo(x, relative ? y + a[0] : a[0]);
          break;
        case "C": {
          const c1x = relative ? x + a[0] : a[0];
          const c1y = relative ? y + a[1] : a[1];
          const c2x = relative ? x + a[2] : a[2];
          const c2y = relative ? y + a[3] : a[3];
          const ex = relative ? x + a[4] : a[4];
          const ey = relative ? y + a[5] : a[5];
          cubicTo(
            points || (begin(), points),
            x,
            y,
            c1x,
            c1y,
            c2x,
            c2y,
            ex,
            ey
          );
          lastControlX = c2x;
          lastControlY = c2y;
          x = ex;
          y = ey;
          break;
        }
        case "S": {
          const smooth = lastCommand === "C" || lastCommand === "S";
          const c1x = smooth ? 2 * x - lastControlX : x;
          const c1y = smooth ? 2 * y - lastControlY : y;
          const c2x = relative ? x + a[0] : a[0];
          const c2y = relative ? y + a[1] : a[1];
          const ex = relative ? x + a[2] : a[2];
          const ey = relative ? y + a[3] : a[3];
          cubicTo(
            points || (begin(), points),
            x,
            y,
            c1x,
            c1y,
            c2x,
            c2y,
            ex,
            ey
          );
          lastControlX = c2x;
          lastControlY = c2y;
          x = ex;
          y = ey;
          break;
        }
        case "Q": {
          const cx = relative ? x + a[0] : a[0];
          const cy = relative ? y + a[1] : a[1];
          const ex = relative ? x + a[2] : a[2];
          const ey = relative ? y + a[3] : a[3];
          quadTo(points || (begin(), points), x, y, cx, cy, ex, ey);
          lastControlX = cx;
          lastControlY = cy;
          x = ex;
          y = ey;
          break;
        }
        case "T": {
          const smooth = lastCommand === "Q" || lastCommand === "T";
          const cx = smooth ? 2 * x - lastControlX : x;
          const cy = smooth ? 2 * y - lastControlY : y;
          const ex = relative ? x + a[0] : a[0];
          const ey = relative ? y + a[1] : a[1];
          quadTo(points || (begin(), points), x, y, cx, cy, ex, ey);
          lastControlX = cx;
          lastControlY = cy;
          x = ex;
          y = ey;
          break;
        }
        case "A": {
          const ex = relative ? x + a[5] : a[5];
          const ey = relative ? y + a[6] : a[6];
          if (!points) begin();
          arcTo(points, x, y, a[0], a[1], a[2], a[3] !== 0, a[4] !== 0, ex, ey);
          x = ex;
          y = ey;
          break;
        }
        default:
          break;
      }

      lastCommand = upper;
    }
  }

  // A subpath of one point draws nothing but confuses the stroke expander.
  return subpaths.filter((sp) => sp.points.length >= 4);
}

const ARG_COUNT = { M: 2, L: 2, H: 1, V: 1, C: 6, S: 4, Q: 4, T: 4, A: 7 };

function cubicTo(points, x0, y0, x1, y1, x2, y2, x3, y3) {
  const n = curveSegments(
    Math.hypot(x1 - x0, y1 - y0) +
      Math.hypot(x2 - x1, y2 - y1) +
      Math.hypot(x3 - x2, y3 - y2)
  );
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    const u = 1 - t;
    const a = u * u * u;
    const b = 3 * u * u * t;
    const c = 3 * u * t * t;
    const e = t * t * t;
    points.push(
      a * x0 + b * x1 + c * x2 + e * x3,
      a * y0 + b * y1 + c * y2 + e * y3
    );
  }
}

function quadTo(points, x0, y0, x1, y1, x2, y2) {
  const n = curveSegments(
    Math.hypot(x1 - x0, y1 - y0) + Math.hypot(x2 - x1, y2 - y1)
  );
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    const u = 1 - t;
    points.push(
      u * u * x0 + 2 * u * t * x1 + t * t * x2,
      u * u * y0 + 2 * u * t * y1 + t * t * y2
    );
  }
}

function curveSegments(controlLength) {
  const n = Math.ceil(Math.sqrt(controlLength / FLATTEN_TOLERANCE));
  return Math.max(2, Math.min(MAX_CURVE_SEGMENTS, n));
}

// Endpoint-parameterised elliptical arc, per the conversion in the SVG
// implementation notes.
function arcTo(points, x0, y0, rx, ry, rotationDeg, largeArc, sweep, x1, y1) {
  if (rx === 0 || ry === 0) {
    points.push(x1, y1);
    return;
  }

  rx = Math.abs(rx);
  ry = Math.abs(ry);

  const phi = (rotationDeg * Math.PI) / 180;
  const cosPhi = Math.cos(phi);
  const sinPhi = Math.sin(phi);

  const dx2 = (x0 - x1) / 2;
  const dy2 = (y0 - y1) / 2;
  const x1p = cosPhi * dx2 + sinPhi * dy2;
  const y1p = -sinPhi * dx2 + cosPhi * dy2;

  // Scale the radii up if they are too small to span the endpoints.
  const lambda = (x1p * x1p) / (rx * rx) + (y1p * y1p) / (ry * ry);
  if (lambda > 1) {
    const s = Math.sqrt(lambda);
    rx *= s;
    ry *= s;
  }

  const sign = largeArc === sweep ? -1 : 1;
  const num = rx * rx * ry * ry - rx * rx * y1p * y1p - ry * ry * x1p * x1p;
  const den = rx * rx * y1p * y1p + ry * ry * x1p * x1p;
  const coef = sign * Math.sqrt(Math.max(0, num / den));

  const cxp = (coef * rx * y1p) / ry;
  const cyp = (-coef * ry * x1p) / rx;

  const cx = cosPhi * cxp - sinPhi * cyp + (x0 + x1) / 2;
  const cy = sinPhi * cxp + cosPhi * cyp + (y0 + y1) / 2;

  const theta = angleBetween(1, 0, (x1p - cxp) / rx, (y1p - cyp) / ry);
  let delta = angleBetween(
    (x1p - cxp) / rx,
    (y1p - cyp) / ry,
    (-x1p - cxp) / rx,
    (-y1p - cyp) / ry
  );

  if (!sweep && delta > 0) delta -= Math.PI * 2;
  else if (sweep && delta < 0) delta += Math.PI * 2;

  const radius = Math.max(rx, ry);
  const n = arcSegments(Math.abs(delta), radius);

  for (let i = 1; i <= n; i++) {
    const t = theta + (delta * i) / n;
    const cosT = Math.cos(t);
    const sinT = Math.sin(t);
    points.push(
      cx + cosPhi * rx * cosT - sinPhi * ry * sinT,
      cy + sinPhi * rx * cosT + cosPhi * ry * sinT
    );
  }
}

function arcSegments(sweepRads, radius) {
  if (radius <= FLATTEN_TOLERANCE) return 2;
  // Segment angle that keeps the sagitta within tolerance.
  const maxAngle = 2 * Math.acos(Math.max(-1, 1 - FLATTEN_TOLERANCE / radius));
  const n = Math.ceil(sweepRads / Math.max(maxAngle, 1e-4));
  return Math.max(2, Math.min(MAX_ARC_SEGMENTS, n));
}

function angleBetween(ux, uy, vx, vy) {
  const dot = ux * vx + uy * vy;
  const len = Math.hypot(ux, uy) * Math.hypot(vx, vy);
  let angle = Math.acos(Math.max(-1, Math.min(1, dot / (len || 1))));
  if (ux * vy - uy * vx < 0) angle = -angle;
  return angle;
}

export { FLATTEN_TOLERANCE };
